import { useState, useEffect } from "react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { BookOpen, Trash2, Loader2, Edit2, X, Check } from "lucide-react";

interface Template {
  id: number;
  supplierName: string;
  identifierKeywords: string;
  vendorName: string;
  defaultCurrency: string | null;
  amountLabel: string | null;
  dateLabel: string | null;
  vatLabel: string | null;
  invoiceLabel: string | null;
  createdAt: string;
}

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

export default function SupplierTemplatesPage() {
  const { toast } = useToast();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editData, setEditData] = useState<Partial<Template>>({});
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${BASE}/api/supplier-templates`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load templates");
      setTemplates(await res.json());
    } catch {
      toast({ title: "Failed to load templates", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    try {
      const res = await fetch(`${BASE}/api/supplier-templates/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to delete");
      toast({ title: "Template deleted" });
      fetchTemplates();
    } catch {
      toast({ title: "Failed to delete template", variant: "destructive" });
    } finally {
      setDeletingId(null);
    }
  };

  const startEdit = (t: Template) => {
    setEditingId(t.id);
    setEditData({
      supplierName: t.supplierName,
      identifierKeywords: t.identifierKeywords,
      vendorName: t.vendorName,
      defaultCurrency: t.defaultCurrency,
      amountLabel: t.amountLabel,
      dateLabel: t.dateLabel,
      vatLabel: t.vatLabel,
      invoiceLabel: t.invoiceLabel,
    });
  };

  const saveEdit = async () => {
    if (!editingId) return;
    try {
      const res = await fetch(`${BASE}/api/supplier-templates/${editingId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(editData),
      });
      if (!res.ok) throw new Error("Failed to update");
      toast({ title: "Template updated" });
      setEditingId(null);
      fetchTemplates();
    } catch {
      toast({ title: "Failed to update template", variant: "destructive" });
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-duffells-grey flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-duffells-orange" />
            Supplier Templates
          </h1>
          <p className="text-duffells-grey/70 mt-1">
            Trained supplier formats for automatic receipt recognition. Create templates from the receipt review page.
          </p>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-duffells-orange" />
          </div>
        ) : templates.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <BookOpen className="w-10 h-10 mx-auto text-duffells-grey/30 mb-3" />
            <p className="text-duffells-grey/60 font-medium">No supplier templates yet</p>
            <p className="text-sm text-duffells-grey/40 mt-1">
              Open a receipt, correct the fields, then click "Train as Supplier Template" to teach the system.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {templates.map((t) => (
              <div key={t.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                {editingId === t.id ? (
                  <div className="p-6 space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Supplier Name</label>
                        <input
                          type="text"
                          value={editData.supplierName || ""}
                          onChange={(e) => setEditData({ ...editData, supplierName: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Vendor Name (output)</label>
                        <input
                          type="text"
                          value={editData.vendorName || ""}
                          onChange={(e) => setEditData({ ...editData, vendorName: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">Identifier Keywords (one per line)</label>
                      <textarea
                        value={editData.identifierKeywords || ""}
                        onChange={(e) => setEditData({ ...editData, identifierKeywords: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-primary/20"
                      />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Currency</label>
                        <input
                          type="text"
                          value={editData.defaultCurrency || ""}
                          onChange={(e) => setEditData({ ...editData, defaultCurrency: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary/20"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Amount Label</label>
                        <input
                          type="text"
                          value={editData.amountLabel || ""}
                          onChange={(e) => setEditData({ ...editData, amountLabel: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary/20"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Date Label</label>
                        <input
                          type="text"
                          value={editData.dateLabel || ""}
                          onChange={(e) => setEditData({ ...editData, dateLabel: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary/20"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">VAT Label</label>
                        <input
                          type="text"
                          value={editData.vatLabel || ""}
                          onChange={(e) => setEditData({ ...editData, vatLabel: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary/20"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Invoice # Label</label>
                        <input
                          type="text"
                          value={editData.invoiceLabel || ""}
                          onChange={(e) => setEditData({ ...editData, invoiceLabel: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary/20"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={saveEdit} className="gap-1.5">
                        <Check className="w-3 h-3" /> Save Changes
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="gap-1.5">
                        <X className="w-3 h-3" /> Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-duffells-grey">{t.supplierName}</h3>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {t.identifierKeywords.split("\n").filter(Boolean).map((kw, i) => (
                            <span key={i} className="px-2 py-0.5 bg-slate-100 rounded text-xs text-duffells-grey/70 font-mono">
                              {kw.trim()}
                            </span>
                          ))}
                        </div>
                        <div className="mt-3 flex flex-wrap gap-4 text-xs text-duffells-grey/60">
                          <span>Vendor: <strong className="text-duffells-grey">{t.vendorName}</strong></span>
                          {t.defaultCurrency && <span>Currency: <strong className="text-duffells-grey">{t.defaultCurrency}</strong></span>}
                          {t.amountLabel && <span>Amount: <strong className="text-duffells-grey">{t.amountLabel}</strong></span>}
                          {t.dateLabel && <span>Date: <strong className="text-duffells-grey">{t.dateLabel}</strong></span>}
                          {t.vatLabel && <span>VAT: <strong className="text-duffells-grey">{t.vatLabel}</strong></span>}
                          {t.invoiceLabel && <span>Invoice: <strong className="text-duffells-grey">{t.invoiceLabel}</strong></span>}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <Button variant="outline" size="sm" onClick={() => startEdit(t)} className="gap-1.5 text-xs">
                          <Edit2 className="w-3 h-3" /> Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={deletingId === t.id}
                          onClick={() => handleDelete(t.id)}
                          className="gap-1.5 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          {deletingId === t.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
